<script setup lang="ts">
import type { DateRange, DateValue } from 'reka-ui';
import type { WeekStartsOn } from './week';
import { PopoverAnchor } from 'reka-ui';
import { getWeekStartsOn } from './week';
import { useRangeCalendarEndModel, useRangeCalendarOneWayModel, useRangeCalendarStartModel } from './models';
import { getMonthCountUpTo, getMonthKey } from './mobile-months';
import { useCalendarState } from './state';
import { useDesktopCalendarPlacement } from './desktop-placement';
import { onBeforeEnterEndTrigger, onEnterEndTrigger, onLeaveEndTrigger } from './end-trigger-transition';
import { DateFormatter, getLocalTimeZone, today } from '@internationalized/date';
import { breakpointsTailwind } from '@vueuse/core';
import { X } from 'lucide-vue-next';

const model = defineModel<DateRange>();

const properties = defineProps<{
  oneWay?: boolean;
  autoOpen?: boolean;
  minDate?: string;
  minDaysBeforeDeparture?: number;
  directFlightDates?: string[];
  returnDirectFlightDates?: string[];
}>();

const emit = defineEmits<{
  'update:autoOpen': [value: false];
  'range-selected': [];
}>();

const { t } = useI18n();
const fullLocale = useFullLocale();
const prefix = 'components.search.datepicker';

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('sm');
const isDesktop = breakpoints.greaterOrEqual('lg');

const df = computed(() => new DateFormatter(fullLocale.value, { weekday: 'short', day: 'numeric', month: '2-digit' }));

const weekStartsOn = computed<WeekStartsOn>(() => getWeekStartsOn(fullLocale.value));

const formatDate = (date: DateValue | undefined): string | undefined => {
  if (!date) return;
  return df.value.format(date.toDate(getLocalTimeZone()));
};

const {
  isOpen,
  mode,
  isStartOpen,
  isEndOpen,
  startSelectionInProgress,
  endSelectionInProgress,
  ensureStartOpen,
  openStart,
  openEnd,
  scheduleClose,
  onStartCalendarMouseenter,
  onStartCalendarMouseleave,
  onEndCalendarMouseenter,
  onEndCalendarMouseleave,
} = useCalendarState({
  model,
  isDesktop,
  oneWay: computed(() => properties.oneWay ?? false),
  onRangeSelected: () => {
    emit('range-selected');
  },
});

const todayDate = today(getLocalTimeZone());
const searchMinDepartureDate = toDateValue(getMinSearchDepartureDateIso(properties.minDaysBeforeDeparture));
const maxDepartureDate = toDateValue(getMaxSearchDepartureDateIso());

// minDate (the previous flight's date in multi-destination) can only narrow the search bound, never widen it.
const minDateBound = computed(() => (properties.minDate ? toDateValue(properties.minDate) : undefined));
const minDepartureDate = computed(() =>
  minDateBound.value && searchMinDepartureDate && minDateBound.value.compare(searchMinDepartureDate) > 0
    ? minDateBound.value
    : searchMinDepartureDate,
);

const endMinValue = computed(() => {
  const start = model.value?.start;
  if (!minDepartureDate.value) return start;
  if (!start || start.compare(minDepartureDate.value) <= 0) return minDepartureDate.value;
  return start;
});

const firstSelectableDate = computed(() => minDepartureDate.value ?? todayDate);

const startMobileScrollTarget = computed(() => model.value?.start ?? firstSelectableDate.value);
const endMobileScrollTarget = computed(() => model.value?.end ?? endMinValue.value ?? todayDate);

const startMobileNumberOfMonths = computed(() =>
  minDepartureDate.value && maxDepartureDate ? getMonthCountUpTo(minDepartureDate.value, maxDepartureDate) : 12,
);

const startOnlyModel = useRangeCalendarStartModel(
  model,
  startSelectionInProgress,
  computed(() => properties.oneWay ?? false),
  () => {
    startSelectionInProgress.value = true;
  },
  () => {
    startSelectionInProgress.value = false;
  },
  scheduleClose,
);
const endOnlyModel = useRangeCalendarEndModel(
  model,
  endSelectionInProgress,
  scheduleClose,
  () => {
    endSelectionInProgress.value = true;
  },
  () => {
    endSelectionInProgress.value = false;
  },
);
const oneWayModel = useRangeCalendarOneWayModel(model);

const startCalendarReference = useTemplateRef<HTMLElement>('startCalendarReference');
const endCalendarReference = useTemplateRef<HTMLElement>('endCalendarReference');
const mobileCalendarScrollReference = useTemplateRef<HTMLElement>('mobileCalendarScrollReference');

const {
  desktopPopoverSide,
  desktopPopoverSideLocked,
  onPopoverPlaced,
  startDesktopPlaceholder,
  onStartDesktopPlaceholderUpdate,
} = useDesktopCalendarPlacement({
  model,
  isOpen,
  isDesktop,
  mode,
  startSelectionInProgress,
  startCalendarReference,
  endCalendarReference,
  firstSelectableDate,
});

useEventListener(startCalendarReference, 'mousemove', onStartCalendarMouseenter);
useEventListener(startCalendarReference, 'mouseleave', onStartCalendarMouseleave);
useEventListener(startCalendarReference, 'focusout', onStartCalendarMouseleave);
useEventListener(endCalendarReference, 'mousemove', onEndCalendarMouseenter);
useEventListener(endCalendarReference, 'mouseleave', onEndCalendarMouseleave);
useEventListener(endCalendarReference, 'focusout', onEndCalendarMouseleave);

watch(
  () => properties.autoOpen,
  (value) => {
    if (!value) {
      return;
    }
    ensureStartOpen();
    emit('update:autoOpen', false);
  },
);

watch(
  isOpen,
  async (open) => {
    if (!open || isDesktop.value) return;
    await nextTick();
    scrollToMobileMonth(mode.value === 'start' ? startMobileScrollTarget.value : endMobileScrollTarget.value);
  },
  { flush: 'post' },
);

const scrollToMobileMonth = (targetMonth: DateValue): void => {
  const container = mobileCalendarScrollReference.value;
  const target = container?.querySelector<HTMLElement>(`[data-calendar-month="${getMonthKey(targetMonth)}"]`);
  target?.scrollIntoView({ block: 'nearest' });
};
</script>

<template>
  <!-- Mobile / Tablet -->
  <div class="lg:hidden">
    <div class="flex gap-2">
      <div
        class="min-w-0 flex-1"
        role="button"
        tabindex="0"
        :aria-label="t(`${prefix}.departureDateTitle`)"
        @click="openStart"
        @keydown.enter="openStart"
      >
        <!-- layout: the slot renders once per layout, so a consumer holding a ref tells the two copies apart -->
        <slot
          name="start"
          :formatted="formatDate(model?.start)"
          :is-open="isStartOpen"
          layout="mobile"
        />
      </div>
      <Transition
        :css="false"
        @before-enter="onBeforeEnterEndTrigger"
        @enter="onEnterEndTrigger"
        @leave="onLeaveEndTrigger"
      >
        <div
          v-if="!oneWay"
          class="min-w-0 flex-1"
          role="button"
          tabindex="0"
          :aria-label="t(`${prefix}.returnDateTitle`)"
          @click="openEnd"
          @keydown.enter="openEnd"
        >
          <slot
            name="end"
            :formatted="formatDate(model?.end)"
            :is-open="isEndOpen"
            layout="mobile"
          />
        </div>
      </Transition>
    </div>

    <UiSheet
      :open="!isDesktop && isOpen"
      @update:open="isOpen = $event"
    >
      <UiSheetContent
        :side="isMobile ? 'bottom' : 'right'"
        hide-close
        class="flex flex-col p-0"
        :class="{ 'h-dvh': isMobile }"
      >
        <div class="flex h-14 shrink-0 items-center shadow-md">
          <span class="min-w-0 flex-1 truncate pl-12 text-center text-lg font-medium text-text-primary-900">
            {{ mode === 'start' ? t(`${prefix}.departureDateTitle`) : t(`${prefix}.returnDateTitle`) }}
          </span>
          <div class="flex justify-end">
            <button
              class="flex h-11 w-11 items-center justify-center rounded-sm"
              :aria-label="t('components.common.close')"
              @click="isOpen = false"
            >
              <X
                class="h-5 w-5 text-gray-600"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <UiCustomRangeCalendarDirectFlightLegend
          v-if="directFlightDates?.length || (!oneWay && returnDirectFlightDates?.length)"
          class="shrink-0 border-b border-gray-200"
        />

        <div
          ref="mobileCalendarScrollReference"
          class="flex flex-1 flex-col items-center overflow-y-auto px-8 py-5"
        >
          <UiCustomRangeCalendarStartContent
            v-if="mode === 'start'"
            v-model:date="oneWayModel"
            v-model:range="startOnlyModel"
            layout="mobile"
            :one-way="oneWay ?? false"
            :locale="fullLocale"
            :week-starts-on="weekStartsOn"
            :min-value="minDepartureDate"
            :max-value="maxDepartureDate"
            :placeholder="firstSelectableDate"
            :number-of-months="startMobileNumberOfMonths"
            :direct-flight-dates="directFlightDates"
          />
          <UiCustomRangeCalendarEndContent
            v-else
            v-model="endOnlyModel"
            layout="mobile"
            :locale="fullLocale"
            :week-starts-on="weekStartsOn"
            :min-value="endMinValue"
            :max-value="maxDepartureDate"
            :placeholder="firstSelectableDate"
            :number-of-months="startMobileNumberOfMonths"
            :ghost-end="endSelectionInProgress ? model?.end : undefined"
            :crescent-start="!!model?.start && !model?.end"
            :direct-flight-dates="returnDirectFlightDates"
          />
        </div>
      </UiSheetContent>
    </UiSheet>
  </div>

  <!-- Desktop -->
  <div class="hidden lg:block lg:w-full">
    <UiPopover
      :open="isDesktop && isOpen"
      @update:open="isOpen = $event"
    >
      <PopoverAnchor as-child>
        <div class="flex w-full gap-2">
          <div
            class="w-full min-w-0"
            role="button"
            tabindex="0"
            :aria-label="t(`${prefix}.departureDateTitle`)"
            @click="openStart"
            @keydown.enter="openStart"
          >
            <slot
              name="start"
              :formatted="formatDate(model?.start)"
              :is-open="isStartOpen"
              layout="desktop"
            />
          </div>
          <Transition
            :css="false"
            @before-enter="onBeforeEnterEndTrigger"
            @enter="onEnterEndTrigger"
            @leave="onLeaveEndTrigger"
          >
            <div
              v-if="!oneWay"
              class="w-full min-w-0"
              role="button"
              tabindex="0"
              :aria-label="t(`${prefix}.returnDateTitle`)"
              @click="openEnd"
              @keydown.enter="openEnd"
            >
              <slot
                name="end"
                :formatted="formatDate(model?.end)"
                :is-open="isEndOpen"
                layout="desktop"
              />
            </div>
          </Transition>
        </div>
      </PopoverAnchor>
      <UiPopoverContent
        class="w-auto p-0"
        :side="desktopPopoverSide"
        :avoid-collisions="!desktopPopoverSideLocked"
        :side-offset="8"
        @open-auto-focus.prevent
        @placed="onPopoverPlaced"
      >
        <div
          v-if="mode === 'start'"
          ref="startCalendarReference"
        >
          <UiCustomRangeCalendarStartContent
            v-model:date="oneWayModel"
            v-model:range="startOnlyModel"
            layout="desktop"
            :one-way="oneWay ?? false"
            :locale="fullLocale"
            :week-starts-on="weekStartsOn"
            :min-value="minDepartureDate"
            :max-value="maxDepartureDate"
            :placeholder="startDesktopPlaceholder"
            :ghost-start="startSelectionInProgress ? model?.start : undefined"
            :direct-flight-dates="directFlightDates"
            @update:placeholder="onStartDesktopPlaceholderUpdate"
          />
        </div>
        <div
          v-else
          ref="endCalendarReference"
        >
          <UiCustomRangeCalendarEndContent
            v-model="endOnlyModel"
            layout="desktop"
            :locale="fullLocale"
            :week-starts-on="weekStartsOn"
            :min-value="endMinValue"
            :max-value="maxDepartureDate"
            :placeholder="model?.end ?? startDesktopPlaceholder"
            :ghost-end="endSelectionInProgress ? model?.end : undefined"
            :direct-flight-dates="returnDirectFlightDates"
          />
        </div>
        <UiCustomRangeCalendarDirectFlightLegend
          v-if="directFlightDates?.length || (!oneWay && returnDirectFlightDates?.length)"
          class="mx-6 border-t border-gray-200"
        />
      </UiPopoverContent>
    </UiPopover>
  </div>
</template>
